import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { auth, db, functionsInstance } from '../../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  doc, getDoc, setDoc, collection, getDocs, updateDoc, serverTimestamp, Timestamp,
  query, orderBy, addDoc, deleteDoc, where, getCountFromServer, limit, startAfter
} from 'firebase/firestore';
import {
  Shield, Bell, Users, Search, RefreshCw, AlertTriangle, CheckCircle, CalendarClock,
  Copy, Download, X, PlusCircle, Trash2, UsersRound, ShoppingCart, ChevronRight, ChevronDown,
  Globe, AlertOctagon, Ban, Check, Eye, ChevronLeft, TrendingUp, Database, Activity, BarChart3
} from 'lucide-react';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';

const createAffiliate = httpsCallable(functionsInstance, 'createAffiliate');
const createAffiliateLink = httpsCallable(functionsInstance, 'createAffiliateLink');
const addAffiliateOwnerFn = httpsCallable(functionsInstance, 'addAffiliateOwner');
const removeAffiliateOwnerFn = httpsCallable(functionsInstance, 'removeAffiliateOwner');
const toggleIpBlockFn = httpsCallable(functionsInstance, 'toggleIpBlock');

const ADMIN_EMAILS = ['info@mrjoelstptgenerator.com', 'conorevans93+4@gmail.com'];

// ---------- tiny format helpers ----------
const money = (n) => {
  const v = Number(n || 0);
  return isFinite(v) ? `$${v.toFixed(2)}` : '$0.00';
};

const dt = (ts) => {
  try {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch { return '—'; }
};

const formatDateDMY = (ts) => {
  try {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '—';
  }
};

// Header
const AdminHeader = () => (
  <header className="bg-white shadow-sm">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center h-16">
        <Link to="/" className="text-xl font-bold text-sky-800">Mr Joel's Seller Tools</Link>
        <Link to="/app" className="text-sm font-medium text-slate-600 hover:text-sky-600">
          &larr; Back to App
        </Link>
      </div>
    </div>
  </header>
);

// Create Link Modal
const CreateLinkModal = ({ affiliate, onClose, onLinkCreated }) => {
  const [campaign, setCampaign] = useState('');
  const [customId, setCustomId] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const [generatedLink, setGeneratedLink] = useState(null);

  const handleGenerateLink = async () => {
    setIsCreating(true);
    setError('');
    setGeneratedLink(null);
    try {
      const result = await createAffiliateLink({
        affiliateId: affiliate.id,
        campaign: campaign || 'general',
        linkId: customId || null,
      });
      setGeneratedLink(result.data.publicUrl);
      if (onLinkCreated) onLinkCreated();
    } catch (err) {
      setError(err.message);
    }
    setIsCreating(false);
  };

  const handleCopyToClipboard = (text) => navigator.clipboard.writeText(text);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="relative bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-slate-600">
          <X size={24} />
        </button>
        <h3 className="text-lg font-semibold text-slate-800">Create Link for {affiliate.name}</h3>
        <div className="mt-4 space-y-4">
          <div>
            <label htmlFor="campaign" className="block text-sm font-medium text-slate-700">Campaign (Optional)</label>
            <input
              id="campaign"
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
              className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm"
              placeholder="e.g., back-to-school"
            />
          </div>
          <div>
            <label htmlFor="customId" className="block text-sm font-medium text-slate-700">Custom Link ID (Optional)</label>
            <input
              id="customId"
              value={customId}
              onChange={(e) => setCustomId(e.target.value)}
              className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm"
              placeholder="e.g., instagram-bio"
            />
          </div>
        </div>
        {generatedLink && (
          <div className="mt-4 p-3 bg-sky-50 border border-sky-200 rounded-md">
            <p className="text-sm font-semibold text-sky-800">Link Generated!</p>
            <div className="mt-2 flex items-center gap-2">
              <input readOnly value={generatedLink} className="w-full bg-white text-sm p-1 border rounded-md font-mono" />
              <button onClick={() => handleCopyToClipboard(generatedLink)} title="Copy Link" className="p-2 text-slate-500 hover:text-sky-600 bg-white rounded-md border">
                <Copy size={16} />
              </button>
            </div>
          </div>
        )}
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleGenerateLink}
            disabled={isCreating}
            className="px-4 py-2 text-sm rounded-md text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50"
          >
            {isCreating ? 'Generating...' : 'Generate Link'}
          </button>
        </div>
      </div>
    </div>
  );
};

// View Links Modal
const ViewLinksModal = ({ affiliate, links, isLoading, onClose }) => {
  const handleCopyToClipboard = (text) => navigator.clipboard.writeText(text);
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="relative bg-white p-6 rounded-lg shadow-xl max-w-3xl w-full">
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"><X size={24} /></button>
        <h3 className="text-lg font-semibold text-slate-800">Existing Links for {affiliate.name}</h3>
        <div className="mt-4 overflow-y-auto max-h-[60vh]">
          {isLoading ? (
            <p className="text-slate-500 text-center py-8">Loading links...</p>
          ) : (
            <div className="space-y-3">
              {links.map(link => (
                <div key={link.id} className="p-3 bg-slate-50 border rounded-md flex items-center justify-between gap-4">
                  <div className="flex-grow">
                    <p className="text-sm font-medium text-slate-800 font-mono break-all">{link.publicUrl}</p>
                    <div className="text-xs text-slate-500 mt-1">
                      <span>Campaign: <span className="font-semibold">{link.campaign}</span></span>
                      <span className="mx-2">|</span>
                      <span>Clicks: <span className="font-semibold">{link.clicks || 0}</span></span>
                    </div>
                  </div>
                  <button onClick={() => handleCopyToClipboard(link.publicUrl)} title="Copy Link" className="p-2 text-slate-500 hover:text-sky-600 bg-white rounded-md border flex-shrink-0">
                    <Copy size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {!isLoading && links.length === 0 && (<p className="text-slate-500 text-center py-8">No links created yet.</p>)}
        </div>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [newNotificationMessage, setNewNotificationMessage] = useState('');
  const [newNotificationId, setNewNotificationId] = useState('');
  const [newNotificationStartDate, setNewNotificationStartDate] = useState('');
  const [newNotificationEndDate, setNewNotificationEndDate] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');

  // Users -- PAGINATION STATE --
  const [users, setUsers] = useState([]);
  const [lastUserDoc, setLastUserDoc] = useState(null);
  const [userPageHistory, setUserPageHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState(''); // The search term actually used for the current data
  const [isFetchingUsers, setIsFetchingUsers] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [usersLoaded, setUsersLoaded] = useState(false); // Manual load tracking

  // Expiration updates
  const [updateUid, setUpdateUid] = useState('');
  const [newExpirationDate, setNewExpirationDate] = useState('');
  const [updateStatusMessage, setUpdateStatusMessage] = useState({ text: '', isError: false });

  // Affiliates
  const [affiliates, setAffiliates] = useState([]);
  const [isFetchingAffiliates, setIsFetchingAffiliates] = useState(false);
  const [newAffiliateName, setNewAffiliateName] = useState('');
  const [newAffiliateId, setNewAffiliateId] = useState('');
  const [affiliateStatus, setAffiliateStatus] = useState('');
  const [linkModalAffiliate, setLinkModalAffiliate] = useState(null);

  // Links modal
  const [linksModalAffiliate, setLinksModalAffiliate] = useState(null);
  const [affiliateLinks, setAffiliateLinks] = useState([]);
  const [isFetchingLinks, setIsFetchingLinks] = useState(false);

  // Live signup counts per affiliate
  const [signupCounts, setSignupCounts] = useState({});

  // Expand rows + referred users
  const [expandedAffiliates, setExpandedAffiliates] = useState({});
  const [affReferredUsers, setAffReferredUsers] = useState({});
  const [affReferredLoading, setAffReferredLoading] = useState({});

  // Per-affiliate editable sales inputs
  const [affSales, setAffSales] = useState({});

  // computed stats
  const [clickTotals, setClickTotals] = useState({});
  const [salesTotals, setSalesTotals] = useState({});
  // const [overall, setOverall] = useState({
  //   clicks: 0, signups: 0, purchases: 0,
  //   gross: 0, netAfterTPT: 0, netMinusComm: 0,
  //   commissionPaid: 0, commissionDue: 0
  // });

  // Download full keyword database as CSV (using backend function)
  const downloadFullKeywordDatabase = async () => {
    if (!currentUser) return;

    try {
      setKeywordStats(prev => ({ ...prev, downloadingCSV: true, downloadError: null }));

      // Call backend function to export CSV data
      const exportFunction = httpsCallable(functionsInstance, 'exportKeywordDatabase');
      const result = await exportFunction();

      if (result.data?.success && result.data?.csvData) {
        // Download CSV
        const blob = new Blob([result.data.csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const timestamp = new Date().toISOString().split('T')[0];
        link.setAttribute('href', url);
        link.setAttribute('download', `Full_Keyword_Database_${timestamp}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setKeywordStats(prev => ({
          ...prev,
          downloadingCSV: false,
          lastDownload: Date.now(),
          downloadError: null
        }));

      } else {
        throw new Error('No data received from export function');
      }

    } catch (error) {
      console.error('Error downloading keyword database:', error);
      setKeywordStats(prev => ({
        ...prev,
        downloadingCSV: false,
        downloadError: error.message || 'Download failed'
      }));
    }
  };
  const [isRefreshingAff, setIsRefreshingAff] = useState(false);

  // --- KEYWORD MANAGEMENT STATE ---
  const [keywordStats, setKeywordStats] = useState({
    totalCached: 0,
    totalTrending: 0,
    withHistory: 0,
    oldestCache: null,
    newestCache: null
  });
  const [keywordUpdateProgress, setKeywordUpdateProgress] = useState({
    isRunning: false,
    progress: 0,
    total: 0,
    updated: 0,
    skipped: 0,
    errors: 0,
    message: ''
  });
  const [keywordStatsLoading, setKeywordStatsLoading] = useState(false);

  // owner management state
  const [ownerEmailInputs, setOwnerEmailInputs] = useState({});
  const [ownerBusy, setOwnerBusy] = useState({});
  const [ownerMsg, setOwnerMsg] = useState({});

  // --- NEW: IP ABUSE STATE ---
  const [ipReports, setIpReports] = useState([]);
  const [isLoadingIps, setIsLoadingIps] = useState(false);
  const [showFullIp, setShowFullIp] = useState({});

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUser(user);
      setIsAdmin(!!user && ADMIN_EMAILS.includes(user.email));
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const fetchAllNotifications = useCallback(async () => {
    setNotificationStatus('Loading...');
    try {
      const notifsQuery = query(collection(db, 'notifications'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(notifsQuery);
      const notifList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setNotifications(notifList);
      setNotificationStatus(`${notifList.length} notification(s) loaded.`);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setNotificationStatus(`Error: ${error.message}`);
    }
  }, []);

  const handleCreateNotification = async () => {
    if (!newNotificationMessage || !newNotificationId) {
      setNotificationStatus('Error: Please provide a message and a unique ID.');
      return;
    }
    setNotificationStatus('Creating...');
    try {
      await addDoc(collection(db, 'notifications'), {
        message: newNotificationMessage,
        notificationId: newNotificationId,
        isActive: true,
        createdAt: serverTimestamp(),
        startDate: newNotificationStartDate ? Timestamp.fromDate(new Date(newNotificationStartDate)) : null,
        endDate: newNotificationEndDate ? Timestamp.fromDate(new Date(newNotificationEndDate)) : null,
        viewCount: 0,
      });
      setNewNotificationMessage('');
      setNewNotificationId('');
      setNewNotificationStartDate('');
      setNewNotificationEndDate('');
      setNotificationStatus('New notification created successfully!');
      fetchAllNotifications();
    } catch (error) {
      console.error("Error creating notification:", error);
      setNotificationStatus(`Error: ${error.message}`);
    }
  };

  const handleToggleNotification = async (notif) => {
    const notifDocRef = doc(db, 'notifications', notif.id);
    try {
      await updateDoc(notifDocRef, { isActive: !notif.isActive });
      fetchAllNotifications();
    } catch (error) {
      console.error("Error toggling notification status:", error);
    }
  };

  const handleDeleteNotification = async (notifId) => {
    if (window.confirm('Delete this notification permanently?')) {
      try {
        await deleteDoc(doc(db, 'notifications', notifId));
        setNotificationStatus('Notification deleted.');
        fetchAllNotifications();
      } catch (error) {
        console.error("Error deleting notification:", error);
      }
    }
  };

  // --- NEW: FETCH IP REPORTS ---
  const fetchIpReports = async () => {
    setIsLoadingIps(true);
    try {
      // Query for IPs where signupCount is > 1
      const q = query(
        collection(db, 'ip_tracking'),
        where('signupCount', '>', 1),
        orderBy('signupCount', 'desc')
      );
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setIpReports(data);
    } catch (e) {
      console.error("Error fetching IP reports:", e);
      // NOTE: You might need to create a Firestore index for this query.
      // Check your browser console for a link to create it if it fails.
    }
    setIsLoadingIps(false);
  };

  // --- NEW: TOGGLE IP BLOCK ---
  const handleToggleBlock = async (ip, currentStatus) => {
    const action = currentStatus ? "UNBLOCK" : "BLOCK";
    if(!window.confirm(`Are you sure you want to ${action} this IP?`)) return;

    try {
      await toggleIpBlockFn({ ip, block: !currentStatus });
      // Update local state to reflect change
      setIpReports(prev => prev.map(item =>
        item.id === ip ? { ...item, blocked: !currentStatus } : item
      ));
    } catch (e) {
      alert(`Failed to ${action} IP: ` + e.message);
    }
  };

  // --- NEW: HELPERS FOR IP TABLE ---
  const renderIp = (ip, id) => {
    if (showFullIp[id]) return ip;
    // Redact first 3 octets: 192.168.1.1 -> XXX.XXX.XXX.1
    const parts = ip.split('.');
    if (parts.length === 4) {
      return `XXX.XXX.XXX.${parts[3]}`;
    }
    // Fallback for IPv6
    return 'XXX... ' + ip.slice(-4);
  };

  const countLast24h = (signupsArray) => {
    if (!Array.isArray(signupsArray)) return 0;
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
    return signupsArray.filter(s => {
      const ts = s.createdAt?.toMillis ? s.createdAt.toMillis() : 0;
      return ts > oneDayAgo;
    }).length;
  };

  // ===============================================
  // === PAGINATED & SEARCHABLE USER FETCHING ===
  // ===============================================

  const fetchUsersPage = async (startAfterDoc = null, searchQuery = null) => {
    if (!isAdmin) return;
    setIsFetchingUsers(true);

    // If searchQuery is passed, use it. Otherwise use currently applied search.
    const effectiveSearch = searchQuery !== null ? searchQuery : appliedSearch;

    try {
      let usersQuery;

      if (effectiveSearch) {
        // --- SEARCH MODE (by Email Prefix) ---
        // Requires index on 'email' (and standard ASC sort)
        const term = effectiveSearch.toLowerCase();
        const endTerm = term + '\uf8ff';

        usersQuery = query(
          collection(db, 'users'),
          where('email', '>=', term),
          where('email', '<=', endTerm),
          orderBy('email'),
          limit(10)
        );

        // Add startAfter if paging
        if (startAfterDoc) {
           usersQuery = query(usersQuery, startAfter(startAfterDoc));
        }

      } else {
        // --- BROWSE MODE (Newest First) ---
        usersQuery = query(
          collection(db, 'users'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );

        // Add optional status filter if no search term
        // (Note: This needs composite index: approvedByAdmin ASC, createdAt DESC)
        if (statusFilter !== 'all') {
             const isApproved = statusFilter === 'approved';
             usersQuery = query(usersQuery, where('approvedByAdmin', '==', isApproved));
        }

        if (startAfterDoc) {
           usersQuery = query(usersQuery, startAfter(startAfterDoc));
        }
      }

      const querySnapshot = await getDocs(usersQuery);
      const userList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setUsers(userList);
      setLastUserDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      setUsersLoaded(true);

    } catch (error) {
      console.error("Error fetching users:", error);
      alert("Error fetching users. Check console (likely missing index).");
    }
    setIsFetchingUsers(false);
  };

  // Called when clicking "Search" or "Refresh"
  const handleSearchOrReload = () => {
    const term = searchTerm.trim().toLowerCase();
    setAppliedSearch(term); // Lock in the search term for pagination
    setUserPageHistory([]); // Reset history
    fetchUsersPage(null, term);
  };

  const handleNextPage = () => {
    if (lastUserDoc) {
        // Push current lastDoc to history so we can go back
        setUserPageHistory(prev => [...prev, lastUserDoc]);
        fetchUsersPage(lastUserDoc);
    }
  };

  const handlePrevPage = () => {
    if (userPageHistory.length === 0) return;

    const newHistory = [...userPageHistory];
    newHistory.pop(); // Remove the doc that started the CURRENT page

    // The new "tail" of history is the doc that started the PREVIOUS page
    // If history is now empty, we are going back to page 1 (null startAfter)
    const prevDoc = newHistory.length > 0 ? newHistory[newHistory.length - 1] : null;

    setUserPageHistory(newHistory);
    fetchUsersPage(prevDoc);
  };

  const approveUserAccess = async (userId) => {
    if (!isAdmin) return;
    if (window.confirm(`Grant full access to ${userId}?`)) {
      try {
        const userDocRef = doc(db, 'users', userId);
        await updateDoc(userDocRef, {
          approvedByAdmin: true,
          accessStatus: 'approved',
          trialEndsAt: null,
          accessExpiresAt: null,
          approvedAt: serverTimestamp(),
        });
        // Refresh Current Page
        const currentStartDoc = userPageHistory.length > 0 ? userPageHistory[userPageHistory.length - 1] : null;
        fetchUsersPage(currentStartDoc);
      } catch (error) {
        console.error(`Error approving user ${userId}:`, error);
        alert(`Failed to approve user: ${error.message}`);
      }
    }
  };

  const handleUpdateExpiration = async () => {
    if (!updateUid || !newExpirationDate) {
      setUpdateStatusMessage({ text: 'Please provide both a User ID and an expiration date.', isError: true });
      return;
    }
    const date = new Date(newExpirationDate);
    if (isNaN(date.getTime())) {
      setUpdateStatusMessage({ text: 'Invalid date format. Use YYYY-MM-DD.', isError: true });
      return;
    }
    date.setHours(23, 59, 59, 999);
    setUpdateStatusMessage({ text: 'Updating...', isError: false });
    try {
      const userDocRef = doc(db, 'users', updateUid);
      await updateDoc(userDocRef, {
        approvedByAdmin: true,
        accessStatus: 'approved',
        trialEndsAt: null,
        accessExpiresAt: Timestamp.fromDate(date),
      });
      setUpdateStatusMessage({ text: `Access for ${updateUid} set to expire on ${date.toLocaleDateString()}.`, isError: false });
      setUpdateUid('');
      setNewExpirationDate('');
    } catch (error) {
      console.error(`Error updating expiration for user ${updateUid}:`, error);
      setUpdateStatusMessage({ text: `Failed to update: ${error.message}`, isError: true });
    }
  };

  // --- KEYWORD MANAGEMENT FUNCTIONS ---
  const fetchKeywordStats = useCallback(async () => {
    if (!currentUser || !ADMIN_EMAILS.includes(currentUser.email)) return;

    setKeywordStatsLoading(true);
    try {
      const getKeywordStatsFn = httpsCallable(functionsInstance, 'getKeywordStats');
      const result = await getKeywordStatsFn();
      setKeywordStats({ ...result.data, _loaded: true, error: null });
    } catch (error) {
      console.error('Error fetching keyword stats:', error);
      setKeywordStats({
        totalCached: 0,
        totalTrending: 0,
        withHistory: null,
        oldestCache: null,
        newestCache: null,
        _loaded: true,
        error: error.message || 'Failed to load stats'
      });
    } finally {
      setKeywordStatsLoading(false);
    }
  }, [currentUser]);

  const runKeywordHistoryUpdate = useCallback(async () => {
    if (!currentUser || !ADMIN_EMAILS.includes(currentUser.email)) return;
    if (keywordUpdateProgress.isRunning) return;

    setKeywordUpdateProgress({
      isRunning: true,
      progress: 0,
      total: 0,
      updated: 0,
      skipped: 0,
      errors: 0,
      opportunitiesCreated: 0,
      message: 'Starting keyword history update...'
    });

    try {
      const runUpdate = httpsCallable(functionsInstance, 'runKeywordHistoryUpdate', { timeout: 130000 });

      let offset = 0;
      let totalUpdated = 0;
      let totalSkipped = 0;
      let totalErrors = 0;
      let totalOpps = 0;
      let isDone = false;

      while (!isDone) {
        const result = await runUpdate({ offset });
        const d = result.data;

        totalUpdated += d.updated;
        totalSkipped += d.skipped;
        totalErrors += d.errors;
        totalOpps += d.opportunitiesCreated || 0;
        isDone = d.isDone;
        offset = d.nextOffset ?? offset;

        setKeywordUpdateProgress({
          isRunning: !isDone,
          progress: d.processedSoFar,
          total: d.totalKeywords,
          updated: totalUpdated,
          skipped: totalSkipped,
          errors: totalErrors,
          opportunitiesCreated: totalOpps,
          message: d.message
        });
      }

      setTimeout(() => fetchKeywordStats(), 1000);

    } catch (error) {
      setKeywordUpdateProgress(prev => ({
        ...prev,
        isRunning: false,
        message: `Update failed: ${error.message}`
      }));
    }
  }, [currentUser, keywordUpdateProgress.isRunning, fetchKeywordStats]);

  // Load keyword stats on mount - REMOVED AUTO-LOADING
  // Stats now load only when "Show Stats" button is clicked

  // ... (Affiliate derived stats functions remain unchanged)
  const sumAffiliateClicks = useCallback(async (affId) => {
    try {
      const linksQuery = query(collection(db, 'affiliates', affId, 'links'));
      const snap = await getDocs(linksQuery);
      const total = snap.docs.reduce((acc, d) => acc + Number(d.data()?.clicks || 0), 0);
      return total;
    } catch (e) {
      console.error('sumAffiliateClicks error', affId, e);
      return 0;
    }
  }, []);

  const readSaleFields = (d) => {
    const sale = Number(d.totalSale ?? d.totalSalePrice ?? 0);
    const commissionRate = Number(d.commissionRate ?? d.commissionRateOverride ?? 0.2);
    const tpt = Number.isFinite(d.tptCommission) ? Number(d.tptCommission) : (sale * 0.20);
    const net = Number.isFinite(d.netAfterTPT) ? Number(d.netAfterTPT) : (sale - (sale * 0.20));
    const commission = Number.isFinite(d.commissionDue) ? Number(d.commissionDue) : (net * commissionRate);
    const paid = !!d.paid;
    return { sale, tpt, net, commissionRate, commission, paid };
  };

  const sumAffiliateSales = useCallback(async (affId) => {
    try {
      const salesSnap = await getDocs(collection(db, 'affiliates', affId, 'sales'));
      let gross = 0, netAfterTPT = 0, commissionTotal = 0, commissionPaid = 0, commissionDue = 0;
      salesSnap.forEach(s => {
        const f = readSaleFields(s.data() || {});
        gross += f.sale;
        netAfterTPT += f.net;
        commissionTotal += f.commission;
        if (f.paid) commissionPaid += f.commission;
        else commissionDue += f.commission;
      });
      return {
        gross,
        netAfterTPT,
        netMinusComm: netAfterTPT - commissionTotal,
        commissionPaid,
        commissionDue,
        commissionTotal
      };
    } catch (e) {
      console.error('sumAffiliateSales error', affId, e);
      return { gross: 0, netAfterTPT: 0, netMinusComm: 0, commissionPaid: 0, commissionDue: 0, commissionTotal: 0 };
    }
  }, []);

  const recomputeOverall = useCallback((affs, signupCountsMap, clicksMap, salesMap) => {
    const clicks = Object.values(clicksMap).reduce((a, b) => a + (b || 0), 0);
    const signups = Object.values(signupCountsMap).reduce((a, b) => a + (b || 0), 0);
    const purchases = affs.reduce((acc, a) => acc + Number(a.totals?.purchases || 0), 0);
    let gross = 0, netAfterTPT = 0, netMinusComm = 0, commissionPaid = 0, commissionDue = 0;
    affs.forEach(a => {
      const s = salesMap[a.id] || {};
      gross += s.gross || 0;
      netAfterTPT += s.netAfterTPT || 0;
      netMinusComm += s.netMinusComm || 0;
      commissionPaid += s.commissionPaid || 0;
      commissionDue += s.commissionDue || 0;
    });
    // setOverall({ clicks, signups, purchases, gross, netAfterTPT, netMinusComm, commissionPaid, commissionDue });
  }, []);

  const refreshAffiliateDerived = useCallback(async (affList, countsOverride = null) => {
    if (!affList || affList.length === 0) {
      setClickTotals({});
      setSalesTotals({});
      // setOverall({ clicks: 0, signups: 0, purchases: 0, gross: 0, netAfterTPT: 0, netMinusComm: 0, commissionPaid: 0, commissionDue: 0 });
      return;
    }
    setIsRefreshingAff(true);
    try {
      const clickPairs = await Promise.all(affList.map(async (a) => [a.id, await sumAffiliateClicks(a.id)]));
      const clicksMap = Object.fromEntries(clickPairs);
      setClickTotals(clicksMap);

      const salesPairs = await Promise.all(affList.map(async (a) => [a.id, await sumAffiliateSales(a.id)]));
      const salesMap = Object.fromEntries(salesPairs);
      setSalesTotals(salesMap);

      recomputeOverall(affList, countsOverride ?? signupCounts, clicksMap, salesMap);
    } finally {
      setIsRefreshingAff(false);
    }
  }, [sumAffiliateClicks, sumAffiliateSales, recomputeOverall, signupCounts]);

  const loadAffiliatesOnce = useCallback(async () => {
    if (!isAdmin) return;
    setIsFetchingAffiliates(true);
    try {
      const affiliatesQuery = query(collection(db, 'affiliates'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(affiliatesQuery);
      const affiliateList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAffiliates(affiliateList);

      const entries = await Promise.all(
        affiliateList.map(async (aff) => {
          const q = query(collection(db, 'referralAttributions'), where('affiliateId', '==', aff.id));
          const snap = await getCountFromServer(q);
          return [aff.id, snap.data().count || 0];
        })
      );
      const counts = Object.fromEntries(entries);
      setSignupCounts(counts);

      await refreshAffiliateDerived(affiliateList, counts);
    } catch (e) {
      console.error('Error loading affiliates:', e);
    } finally {
      setIsFetchingAffiliates(false);
    }
  }, [isAdmin, refreshAffiliateDerived]);

  const handleCreateAffiliate = async () => {
    if (!newAffiliateName || !newAffiliateId) {
      setAffiliateStatus('Error: Affiliate Name and a unique ID are required.');
      return;
    }
    setAffiliateStatus('Creating...');
    try {
      await createAffiliate({ name: newAffiliateName, affiliateId: newAffiliateId });
      setNewAffiliateName('');
      setNewAffiliateId('');
      setAffiliateStatus('Affiliate created successfully!');
      loadAffiliatesOnce();
    } catch (error) {
      console.error("Error creating affiliate:", error);
      setAffiliateStatus(`Error: ${error.message}`);
    }
  };

  const loadReferredUsersForAffiliate = async (affiliateId) => {
    setAffReferredLoading(prev => ({ ...prev, [affiliateId]: true }));
    try {
      const attributionsQuery = query(
        collection(db, 'referralAttributions'),
        where('affiliateId', '==', affiliateId)
      );
      const attributionSnapshot = await getDocs(attributionsQuery);

      const attrRows = attributionSnapshot.docs.map(d => {
        const data = d.data() || {};
        const uid = data.userId || d.id;
        const signedUpAt = data.attributedAt || data.createdAt || null;
        const emailHint = data.userEmail || data.email || null;
        return { uid, signedUpAt, emailHint };
      });

      const uids = Array.from(new Set(attrRows.map(r => r.uid).filter(Boolean)));
      const userDocs = await Promise.all(uids.map(uid => getDoc(doc(db, 'users', uid))));
      const usersMap = {};
      userDocs.forEach(docSnap => {
        if (docSnap.exists()) usersMap[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
      });

      const merged = attrRows.map(r => {
        const u = usersMap[r.uid];
        return u
          ? {
            id: u.id,
            email: u.email || r.emailHint || '(no email)',
            approvedByAdmin: !!u.approvedByAdmin,
            createdAt: u.createdAt || null,
            approvedAt: u.approvedAt || null,
            signedUpAt: r.signedUpAt || u.createdAt || null,
          }
          : {
            id: r.uid,
            email: r.emailHint || '(no email)',
            approvedByAdmin: false,
            createdAt: null,
            approvedAt: null,
            signedUpAt: r.signedUpAt || null,
          };
      });

      setAffReferredUsers(prev => ({ ...prev, [affiliateId]: merged }));

      const salesSnap = await getDocs(collection(db, 'affiliates', affiliateId, 'sales'));
      const initial = {};
      salesSnap.forEach(s => {
        const data = s.data();
        const uid = data.userId || s.id;
        initial[uid] = {
          totalSale: data.totalSale ?? data.totalSalePrice ?? '',
          commissionRate: data.commissionRate ?? data.commissionRateOverride ?? 0.2,
          paid: !!data.paid,
        };
      });
      setAffSales(prev => ({ ...prev, [affiliateId]: { ...(prev[affiliateId] || {}), ...initial } }));
    } catch (e) {
      console.error('Error loading referred users/sales for affiliate', affiliateId, e);
      setAffReferredUsers(prev => ({ ...prev, [affiliateId]: [] }));
      setAffSales(prev => ({ ...prev, [affiliateId]: {} }));
    } finally {
      setAffReferredLoading(prev => ({ ...prev, [affiliateId]: false }));
    }
  };

  const toggleAffiliateExpand = async (aff) => {
    setExpandedAffiliates(prev => {
      const next = { ...prev, [aff.id]: !prev[aff.id] };
      return next;
    });
    if (!expandedAffiliates[aff.id]) {
      await loadReferredUsersForAffiliate(aff.id);
    }
  };

  const getSaleRow = (affiliateId, userId) =>
    (affSales[affiliateId]?.[userId]) || { totalSale: '', commissionRate: 0.2, paid: false };

  const setSaleRow = (affiliateId, userId, patch) => {
    setAffSales(prev => ({
      ...prev,
      [affiliateId]: {
        ...(prev[affiliateId] || {}),
        [userId]: { ...getSaleRow(affiliateId, userId), ...patch }
      }
    }));
  };

  const computeValues = (totalSale, commissionRate) => {
    const sale = Number.parseFloat(totalSale ?? 0) || 0;
    const rate = Number.parseFloat(commissionRate ?? 0) || 0;
    const tptFee = sale * 0.20;
    const netAfterTPT = sale - tptFee;
    const commissionDue = netAfterTPT * rate;
    return { tptFee, netAfterTPT, commissionDue };
  };

  const saveSale = async (affiliateId, userObj) => {
    const userId = userObj.id;
    const row = getSaleRow(affiliateId, userId);
    const sale = Number.parseFloat(row.totalSale ?? 0) || 0;
    const rate = Number.parseFloat(row.commissionRate ?? 0) || 0.2;
    const { tptFee, netAfterTPT, commissionDue } = computeValues(sale, rate);

    try {
      await setDoc(
        doc(db, 'affiliates', affiliateId, 'sales', userId),
        {
          affiliateId,
          userId,
          totalSale: sale,
          commissionRate: rate,
          tptCommission: tptFee,
          netAfterTPT,
          commissionDue,
          paid: !!row.paid,
          updatedAt: serverTimestamp(),
          userEmail: userObj.email || null,
          approvedAt: userObj.approvedAt || null,
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );
      const clicks = clickTotals[affiliateId] ?? 0;
      const sales = await sumAffiliateSales(affiliateId);
      setSalesTotals(prev => ({ ...prev, [affiliateId]: sales }));
      recomputeOverall(affiliates, signupCounts, { ...clickTotals, [affiliateId]: clicks }, { ...salesTotals, [affiliateId]: sales });
    } catch (e) {
      console.error('Error saving sale row:', e);
      alert('Failed to save sale row: ' + e.message);
    }
  };

  const setManualApprovedAt = async (userId) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        approvedAt: serverTimestamp(),
        approvedByAdmin: true,
        accessStatus: 'approved'
      });
      // Refresh
    } catch (e) {
      console.error('Error setting approvedAt:', e);
      alert('Failed to set Purchase Confirmed: ' + e.message);
    }
  };

  const handleViewLinks = async (affiliate) => {
    setLinksModalAffiliate(affiliate);
    setIsFetchingLinks(true);
    setAffiliateLinks([]);
    try {
      const linksQuery = query(collection(db, 'affiliates', affiliate.id, 'links'), orderBy('createdAt', 'desc'));
      const linksSnapshot = await getDocs(linksQuery);
      const linksData = linksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAffiliateLinks(linksData);
    } catch (error) {
      console.error("Error fetching affiliate links:", error);
    }
    setIsFetchingLinks(false);
  };

  const handleOwnerInputChange = (affId, v) => {
    setOwnerEmailInputs(prev => ({ ...prev, [affId]: v }));
  };

  const handleAddOwner = async (aff) => {
    const email = (ownerEmailInputs[aff.id] || '').trim();
    if (!email) {
      setOwnerMsg(prev => ({ ...prev, [aff.id]: 'Enter an email to add.' }));
      return;
    }
    setOwnerBusy(prev => ({ ...prev, [aff.id]: true }));
    setOwnerMsg(prev => ({ ...prev, [aff.id]: 'Adding owner…' }));
    try {
      await addAffiliateOwnerFn({ affiliateId: aff.id, email });
      setOwnerMsg(prev => ({ ...prev, [aff.id]: 'Owner added.' }));
      setOwnerEmailInputs(prev => ({ ...prev, [aff.id]: '' }));
      await loadAffiliatesOnce();
    } catch (e) {
      console.error('add owner error', e);
      setOwnerMsg(prev => ({ ...prev, [aff.id]: e.message || 'Failed to add owner' }));
    } finally {
      setOwnerBusy(prev => ({ ...prev, [aff.id]: false }));
      setTimeout(() => setOwnerMsg(prev => ({ ...prev, [aff.id]: '' })), 2500);
    }
  };

  const handleRemoveOwner = async (aff, ownerEmail) => {
    if (!window.confirm(`Remove ${ownerEmail} as owner of ${aff.name}?`)) return;
    setOwnerBusy(prev => ({ ...prev, [aff.id]: true }));
    setOwnerMsg(prev => ({ ...prev, [aff.id]: 'Removing owner…' }));
    try {
      await removeAffiliateOwnerFn({ affiliateId: aff.id, email: ownerEmail });
      setOwnerMsg(prev => ({ ...prev, [aff.id]: 'Owner removed.' }));
      await loadAffiliatesOnce();
    } catch (e) {
      console.error('remove owner error', e);
      setOwnerMsg(prev => ({ ...prev, [aff.id]: e.message || 'Failed to remove owner' }));
    } finally {
      setOwnerBusy(prev => ({ ...prev, [aff.id]: false }));
      setTimeout(() => setOwnerMsg(prev => ({ ...prev, [aff.id]: '' })), 2500);
    }
  };

  const handleManualRefresh = () => {
    loadAffiliatesOnce();
  };

  useEffect(() => {
    if (isAdmin) {
      fetchAllNotifications();
      fetchIpReports();
      // fetchFirstPage();  <-- REMOVED AUTOMATIC LOAD
    }
  }, [isAdmin, fetchAllNotifications]);

  // ------- downloads --------
  const downloadFile = (content, fileName, contentType) => {
    const blob = new Blob([content], { type: contentType });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const csvEscape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

  const buildAffiliateCSV = async (aff) => {
    const clicks = clickTotals[aff.id] ?? await sumAffiliateClicks(aff.id);
    const sales = salesTotals[aff.id] ?? await sumAffiliateSales(aff.id);

    let usersList = affReferredUsers[aff.id];
    if (!usersList) {
      await loadReferredUsersForAffiliate(aff.id);
      usersList = affReferredUsers[aff.id] || [];
    }

    const salesSnap = await getDocs(collection(db, 'affiliates', aff.id, 'sales'));
    const salesMap = {};
    salesSnap.forEach(s => { salesMap[s.id] = { id: s.id, ...s.data() }; });

    let rows = [];
    rows.push(['Affiliate Name', 'Affiliate ID', 'Clicks (sum of links)', 'Signups', 'Purchases', 'Gross Sales', 'Net After TPT', 'Net - Commission', 'Commission Paid', 'Commission Due']
      .map(csvEscape).join(','));
    rows.push([
      aff.name, aff.id, clicks,
      signupCounts[aff.id] ?? 0,
      aff.totals?.purchases || 0,
      (sales.gross || 0).toFixed(2),
      (sales.netAfterTPT || 0).toFixed(2),
      (sales.netMinusComm || 0).toFixed(2),
      (sales.commissionPaid || 0).toFixed(2),
      (sales.commissionDue || 0).toFixed(2),
    ].map(csvEscape).join(','));

    rows.push('');
    rows.push(['User Email', 'UID', 'Status', 'Signed Up', 'Purchase Confirmed (Approved At)', 'Total Sale', 'TPT Fee (20%)', 'Net After TPT', 'Rate', 'Commission', 'Paid']
      .map(csvEscape).join(','));

    (usersList || []).forEach(u => {
      const sale = salesMap[u.id] || {};
      const f = readSaleFields(sale);
      rows.push([
        u.email, u.id,
        u.approvedByAdmin ? 'Purchased' : 'Trial',
        dt(u.signedUpAt || u.createdAt),
        u.approvedAt ? dt(u.approvedAt) : '',
        f.sale.toFixed(2),
        f.tpt.toFixed(2),
        f.net.toFixed(2),
        f.commissionRate,
        f.commission.toFixed(2),
        sale.paid ? 'YES' : 'NO'
      ].map(csvEscape).join(','));
    });

    return rows.join('\n');
  };

  const handleDownloadAffiliateCSV = async (aff) => {
    const csv = await buildAffiliateCSV(aff);
    downloadFile(csv, `affiliate_${aff.id}.csv`, 'text/csv;charset=utf-8;');
  };

  const handleDownloadAllAffiliatesCSV = async () => {
    let parts = [];
    for (const aff of affiliates) {
      const csv = await buildAffiliateCSV(aff);
      parts.push(csv, '\n\n');
    }
    downloadFile(parts.join(''), 'affiliates_all.csv', 'text/csv;charset=utf-8;');
  };

  const handleDownloadUsersCSV = () => {
    let csvContent = "Email,Access Status,Expiration Date,Created Date,Approved Date\n";
    const filteredUsers = users
      .filter(user => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'approved') return user.approvedByAdmin === true;
        if (statusFilter === 'trial') return !user.approvedByAdmin;
        return true;
      });

    filteredUsers.forEach(user => {
      const status = user.approvedByAdmin ? 'Approved' : 'Trial';
      const expiration = user.accessExpiresAt
        ? formatDateDMY(user.accessExpiresAt)
        : 'N/A';
      const createdDate = user.createdAt ? formatDateDMY(user.createdAt) : '';
      const approvedDate = user.approvedAt ? formatDateDMY(user.approvedAt) : '';

      csvContent += `"${user.email}","${status}","${expiration}","${createdDate}","${approvedDate}"\n`;
    });

    downloadFile(csvContent, 'tpt_users.csv', 'text/csv;charset=utf-8;');
  };

  const handleDownloadEmails = () => {
    const filteredUsers = users
      .filter(user => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'approved') return user.approvedByAdmin === true;
        if (statusFilter === 'trial') return !user.approvedByAdmin;
        return true;
      });

    let txtContent = "Email\tStatus\tCreated\tApproved\n";

    filteredUsers.forEach(user => {
      const status = user.approvedByAdmin ? 'Approved' : 'Trial';
      const createdDate = user.createdAt ? formatDateDMY(user.createdAt) : '';
      const approvedDate = user.approvedAt ? formatDateDMY(user.approvedAt) : '';
      txtContent += `${user.email}\t${status}\t${createdDate}\t${approvedDate}\n`;
    });

    downloadFile(txtContent, 'user_emails.txt', 'text/plain;charset=utf-8;');
  };

  if (isLoading) return <div className="flex items-center justify-center h-screen">Loading...</div>;

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-100">
        <AlertTriangle className="w-16 h-16 text-red-500 mb-4" />
        <h1 className="text-2xl font-bold text-slate-800">Access Denied</h1>
        <p className="text-slate-600 mt-2">You do not have permission to view this page.</p>
        <Link to="/" className="mt-6 text-sky-600 hover:underline">Go back to the homepage</Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Dashboard | Mr. Joel's TPT Tools</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {linkModalAffiliate && (
        <CreateLinkModal
          affiliate={linkModalAffiliate}
          onClose={() => setLinkModalAffiliate(null)}
          onLinkCreated={() => loadAffiliatesOnce()}
        />
      )}

      {linksModalAffiliate && (
        <ViewLinksModal
          affiliate={linksModalAffiliate}
          links={affiliateLinks}
          isLoading={isFetchingLinks}
          onClose={() => setLinksModalAffiliate(null)}
        />
      )}

      <div className="min-h-screen bg-slate-100">
        <AdminHeader />
        <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center">
            <Shield className="w-8 h-8 mr-3 text-sky-700" />
            Admin Dashboard
          </h1>

          {/* IP ABUSE SECTION (Unchanged) */}
          <section className="mt-8 bg-white p-6 rounded-lg shadow-md border border-slate-200">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                <AlertOctagon className="w-6 h-6 mr-2 text-sky-600" />
                IP Abuse Monitor
              </h2>
              <button onClick={fetchIpReports} className="p-2 bg-slate-100 hover:bg-slate-200 rounded-md text-slate-600"><RefreshCw className={`w-5 h-5 ${isLoadingIps ? 'animate-spin' : ''}`} /></button>
            </div>
            {/* ... IP Table Content ... */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase text-left">Network (IP)</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase text-center">Country</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase text-center">Total Accounts</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase text-center">Last 24h</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase text-left">Associated Emails</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {ipReports.map((report) => (
                      <tr key={report.id} className={report.blocked ? "bg-red-50" : ""}>
                        <td className="px-4 py-3 text-sm font-mono text-slate-700">
                          <div className="flex items-center gap-2">
                            <span>{renderIp(report.id, report.id)}</span>
                            <button onClick={() => setShowFullIp(prev => ({...prev, [report.id]: !prev[report.id]}))} className="text-slate-400 hover:text-sky-600"><Eye size={14} /></button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-xs"><Globe size={12} /> {report.country || 'Unknown'}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center font-bold text-slate-800">{report.signupCount}</td>
                        <td className="px-4 py-3 text-sm text-center">{(countLast24h(report.signups) > 0) ? <span className="text-red-600 font-bold">+{countLast24h(report.signups)}</span> : <span className="text-slate-400">0</span>}</td>
                        <td className="px-4 py-3 text-xs text-slate-600">
                          <div className="max-h-20 overflow-y-auto">{report.signups.slice(0,3).map(s=> <div key={s.email}>{s.email}</div>)}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => handleToggleBlock(report.id, report.blocked)} className={`flex items-center justify-center w-full px-3 py-1.5 text-xs font-bold rounded shadow-sm ${report.blocked ? 'bg-slate-600 text-white' : 'bg-red-100 text-red-700 border border-red-200'}`}>
                            {report.blocked ? <><Check size={14} className="mr-1" /> UNBLOCK</> : <><Ban size={14} className="mr-1" /> BLOCK</>}
                          </button>
                        </td>
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ==================================================== */}
          {/* ========== KEYWORD MANAGEMENT SECTION ============ */}
          {/* ==================================================== */}
          <section className="mt-8 bg-white p-6 rounded-lg shadow-md border">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                  <TrendingUp className="w-6 h-6 mr-2 text-sky-600" />
                  Keyword Research Management
                </h2>
                <p className="text-sm text-slate-500 mt-1">Manage keyword cache and historical trend data</p>
              </div>
              <button
                onClick={fetchKeywordStats}
                disabled={keywordStatsLoading}
                className="p-2 text-slate-500 hover:text-slate-800 disabled:opacity-50 bg-slate-100 rounded-md"
                title="Refresh Stats"
              >
                <RefreshCw className={`w-5 h-5 ${keywordStatsLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Keyword Stats Overview */}
            {!keywordStats._loaded && !keywordStatsLoading ? (
              <div className="text-center py-8">
                <button
                  onClick={fetchKeywordStats}
                  disabled={keywordStatsLoading}
                  className="px-6 py-3 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-medium transition-colors"
                >
                  <span className="flex items-center">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Show Keyword Stats
                  </span>
                </button>
                <p className="text-sm text-slate-500 mt-2">Click to load keyword cache statistics</p>
              </div>
            ) : keywordStatsLoading ? (
              <div className="text-center py-8">
                <span className="flex items-center justify-center text-slate-500">
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Loading Stats...
                </span>
              </div>
            ) : keywordStats.error ? (
              <div className="text-center py-8">
                <p className="text-sm text-red-600 mb-3">⚠️ {keywordStats.error}</p>
                <button onClick={fetchKeywordStats} className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-medium text-sm">
                  Retry
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-sky-50 p-4 rounded-lg border border-sky-200">
                <div className="flex items-center">
                  <Database className="w-8 h-8 text-sky-600 mr-3" />
                  <div>
                    <p className="text-sm text-slate-600">Total Cached</p>
                    <p className="text-2xl font-bold text-sky-700">{keywordStats.totalCached}</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center">
                  <Activity className="w-8 h-8 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm text-slate-600">Trending Active</p>
                    <p className="text-2xl font-bold text-green-700">{keywordStats.totalTrending}</p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center">
                  <BarChart3 className="w-8 h-8 text-purple-600 mr-3" />
                  <div>
                    <p className="text-sm text-slate-600">With History</p>
                    <p className="text-2xl font-bold text-purple-700">{keywordStats.withHistory ?? '—'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="text-center">
                  <p className="text-sm text-slate-600">Oldest Cache</p>
                  <p className="text-xs font-medium text-amber-700">
                    {keywordStats.oldestCache ? new Date(keywordStats.oldestCache).toLocaleDateString() : '—'}
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="text-center">
                  <p className="text-sm text-slate-600">Newest Cache</p>
                  <p className="text-xs font-medium text-slate-700">
                    {keywordStats.newestCache ? new Date(keywordStats.newestCache).toLocaleDateString() : '—'}
                  </p>
                </div>
              </div>
            </div>            )}

            {/* Full Database Download Section - only show when stats are loaded */}
            {keywordStats._loaded && !keywordStats.error && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200 mb-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-md font-semibold text-slate-800 flex items-center mb-1">
                    <Download className="w-4 h-4 mr-2 text-green-600" />
                    Full Database Export
                  </h3>
                  <p className="text-sm text-slate-600">
                    Download complete keyword database with Golden Scores, popularity, competition, dates, and search history
                  </p>
                </div>
                <button
                  onClick={downloadFullKeywordDatabase}
                  disabled={keywordStats.downloadingCSV}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    keywordStats.downloadingCSV
                      ? 'bg-slate-400 text-white cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {keywordStats.downloadingCSV ? (
                    <span className="flex items-center">
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Downloading...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Download className="w-4 h-4 mr-2" />
                      Download CSV
                    </span>
                  )}
                </button>
              </div>

              {keywordStats.downloadError && (
                <p className="text-sm text-red-600 mt-2">⚠️ {keywordStats.downloadError}</p>
              )}

              {keywordStats.lastDownload && (
                <p className="text-xs text-green-600 mt-2">
                  ✅ Last downloaded: {new Date(keywordStats.lastDownload).toLocaleString()}
                </p>
              )}
            </div>
            )}

            {/* History Update Section - only show when stats are loaded */}
            {keywordStats._loaded && !keywordStats.error && (
            <div className="bg-gradient-to-r from-blue-50 to-sky-50 p-6 rounded-lg border border-sky-200">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                    <Database className="w-5 h-5 mr-2 text-sky-600" />
                    Weekly Historical Data Update
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Run this weekly to create historical snapshots of cached keywords for trend tracking
                  </p>
                </div>

                <button
                  onClick={runKeywordHistoryUpdate}
                  disabled={keywordUpdateProgress.isRunning}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    keywordUpdateProgress.isRunning
                      ? 'bg-slate-400 text-white cursor-not-allowed'
                      : 'bg-sky-600 hover:bg-sky-700 text-white'
                  }`}
                >
                  {keywordUpdateProgress.isRunning ? (
                    <span className="flex items-center">
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Update History
                    </span>
                  )}
                </button>
              </div>

              {/* Progress Display */}
              {(keywordUpdateProgress.isRunning || keywordUpdateProgress.message) && (
                <div className="mt-4">
                  {keywordUpdateProgress.isRunning && keywordUpdateProgress.total > 0 && (
                    <div className="mb-3">
                      <div className="flex justify-between text-sm text-slate-600 mb-1">
                        <span>Progress: {keywordUpdateProgress.progress} / {keywordUpdateProgress.total}</span>
                        <span>{Math.round((keywordUpdateProgress.progress / keywordUpdateProgress.total) * 100)}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div
                          className="bg-sky-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(keywordUpdateProgress.progress / keywordUpdateProgress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <div className="bg-white p-2 rounded border">
                      <span className="text-green-600 font-semibold">{keywordUpdateProgress.updated}</span>
                      <span className="text-slate-600 ml-1">Updated</span>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <span className="text-amber-600 font-semibold">{keywordUpdateProgress.skipped}</span>
                      <span className="text-slate-600 ml-1">Skipped</span>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <span className="text-purple-600 font-semibold">{keywordUpdateProgress.opportunitiesCreated || 0}</span>
                      <span className="text-slate-600 ml-1">Opps Added</span>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <span className="text-red-600 font-semibold">{keywordUpdateProgress.errors}</span>
                      <span className="text-slate-600 ml-1">Errors</span>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <span className="text-slate-600 font-semibold">{keywordUpdateProgress.progress}</span>
                      <span className="text-slate-600 ml-1">Total</span>
                    </div>
                  </div>

                  {keywordUpdateProgress.message && (
                    <p className={`mt-3 text-sm ${
                      keywordUpdateProgress.message.includes('failed') || keywordUpdateProgress.message.includes('error')
                        ? 'text-red-600' 
                        : keywordUpdateProgress.isRunning 
                          ? 'text-sky-600' 
                          : 'text-green-600'
                    }`}>
                      {keywordUpdateProgress.message}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-4 text-xs text-slate-500 bg-white p-3 rounded border">
                <p className="font-medium mb-1">💡 How this works:</p>
                <ul className="space-y-1">
                  <li>• Scans all cached keywords for historical data opportunities</li>
                  <li>• Saves current cache data as historical snapshots before 7-day refresh</li>
                  <li>• Builds month-over-month trend data for popularity tracking</li>
                  <li>• Run weekly to maintain consistent historical dataset</li>
                  <li>• Keywords with existing history are skipped to prevent duplicates</li>
                </ul>
              </div>
            </div>
            )}
          </section>

          {/* ==================================================== */}
          {/* =========== USER MANAGEMENT SECTION ============= */}
          {/* ==================================================== */}
          <section className="mt-8 bg-white p-6 rounded-lg shadow-md border">
            <div className="flex flex-wrap gap-4 justify-between items-center">
              <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                <Users className="w-6 h-6 mr-2 text-sky-600" />
                User Management
              </h2>
              <div className="flex items-center gap-2">
                <button onClick={handleDownloadEmails} title="Download emails as TXT" className="p-2 text-slate-500 hover:text-sky-600 bg-slate-100 rounded-md"><Download size={18} /></button>
                <button onClick={handleDownloadUsersCSV} title="Download users CSV" className="p-2 text-slate-500 hover:text-sky-600 bg-slate-100 rounded-md"><Download size={18} /></button>
                <button
                    onClick={handleSearchOrReload}
                    disabled={isFetchingUsers}
                    className="p-2 text-slate-500 hover:text-slate-800 disabled:opacity-50 bg-slate-100 rounded-md"
                    title="Reload / Search"
                >
                  <RefreshCw className={`w-5 h-5 ${isFetchingUsers ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="relative flex gap-2">
                <div className="relative flex-grow">
                    <input
                        type="search"
                        placeholder="Search by email prefix..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearchOrReload()}
                        className="w-full p-2 pl-10 border border-slate-300 rounded-md bg-white text-slate-900 placeholder-slate-400 focus:ring-1 focus:ring-sky-500 sm:text-sm"
                    />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                </div>
                <button
                    onClick={handleSearchOrReload}
                    className="px-3 py-2 bg-sky-600 text-white text-sm font-medium rounded-md hover:bg-sky-700"
                >
                    Search
                </button>
              </div>

              {/* Pagination Controls */}
              <div className="flex justify-end items-center gap-2">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500 mr-2">Page {userPageHistory.length + 1}</span>
                    <button
                        onClick={handlePrevPage}
                        disabled={userPageHistory.length === 0 || isFetchingUsers}
                        className="p-2 border rounded hover:bg-slate-50 disabled:opacity-50 text-slate-600"
                        title="Previous Page"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        onClick={handleNextPage}
                        disabled={users.length < 10 || isFetchingUsers} // Disable next if we received fewer than limit
                        className="p-2 border rounded hover:bg-slate-50 disabled:opacity-50 text-slate-600"
                        title="Next Page"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>

                <div>
                    <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-900 focus:ring-1 focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    >
                    <option value="all">All Users</option>
                    <option value="approved">Full Access</option>
                    <option value="trial">Trial</option>
                    </select>
                </div>
              </div>
            </div>

            <div className="mt-4 overflow-x-auto">
              {!usersLoaded ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                    <p className="text-slate-500 mb-4">User list is hidden to save database reads.</p>
                    <button
                        onClick={handleSearchOrReload}
                        className="px-4 py-2 bg-white border border-slate-300 rounded-md shadow-sm text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                        Load Recent Users
                    </button>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                    <tr>
                        <th className="px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center">User Email</th>
                        <th className="px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center">Access Status</th>
                        <th className="px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center">Created</th>
                        <th className="px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center">Approved</th>
                        <th className="px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center">User ID (UID)</th>
                        <th className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                        </th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                    {users.map(user => (
                        <tr key={user.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{user.email}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                            {user.approvedByAdmin ? (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Approved {user.accessExpiresAt ? `(Expires: ${user.accessExpiresAt.toDate().toLocaleDateString()})` : ''}
                                </span>
                            ) : (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">Trial</span>
                            )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-slate-700">
                            {user.createdAt ? formatDateDMY(user.createdAt) : '—'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-slate-700">
                            {user.approvedAt ? formatDateDMY(user.approvedAt) : '—'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 font-mono flex items-center gap-2 justify-center">
                            <span>{user.id}</span>
                            <button onClick={() => navigator.clipboard.writeText(user.id)} title="Copy UID" className="p-1 text-slate-400 hover:text-slate-700">
                                <Copy size={14} />
                            </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {!user.approvedByAdmin && (
                                <button onClick={() => approveUserAccess(user.id)} className="text-green-600 hover:text-green-900 flex items-center">
                                <CheckCircle className="w-4 h-4 mr-1" /> Approve Full Access
                                </button>
                            )}
                            </td>
                        </tr>
                        ))}
                    </tbody>
                </table>
              )}
              {usersLoaded && users.length === 0 && (
                <p className="text-center py-8 text-slate-500">
                  {isFetchingUsers ? 'Loading users...' : 'No users found matching criteria.'}
                </p>
              )}
            </div>
          </section>

          {/* ... (Affiliate Management, Notifications, Update Expiration sections kept unchanged below) ... */}

          <section className="mt-8 bg-white p-6 rounded-lg shadow-md border">
            {/* Affiliate Management Content (same as provided, no changes needed for this fix) */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                <UsersRound className="w-6 h-6 mr-2 text-sky-600" />
                Affiliate Management
              </h2>
              {/* ... (rest of affiliate UI) ... */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleManualRefresh}
                  disabled={isRefreshingAff || isFetchingAffiliates}
                  className="p-2 text-slate-600 hover:text-slate-900 bg-slate-100 rounded-md disabled:opacity-50"
                >
                  <RefreshCw className={`w-5 h-5 ${isRefreshingAff || isFetchingAffiliates ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Create New Affiliate Form */}
            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                <PlusCircle className="w-5 h-5 mr-2 text-blue-600" />
                Create New Affiliate
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="newAffiliateName" className="block text-sm font-medium text-slate-700">
                    Affiliate Name
                  </label>
                  <input
                    id="newAffiliateName"
                    type="text"
                    value={newAffiliateName}
                    onChange={(e) => setNewAffiliateName(e.target.value)}
                    placeholder="e.g., John's Education Store"
                    className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="newAffiliateId" className="block text-sm font-medium text-slate-700">
                    Affiliate ID (unique)
                  </label>
                  <input
                    id="newAffiliateId"
                    type="text"
                    value={newAffiliateId}
                    onChange={(e) => setNewAffiliateId(e.target.value)}
                    placeholder="e.g., john-edu-store"
                    className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleCreateAffiliate}
                    disabled={!newAffiliateName || !newAffiliateId}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white rounded-md font-medium transition-colors"
                  >
                    Create Affiliate
                  </button>
                </div>
              </div>
              {affiliateStatus && (
                <div className={`mt-3 text-sm ${affiliateStatus.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
                  {affiliateStatus}
                </div>
              )}
            </div>

            {/* Affiliate List Table */}
            <div className="mt-6 overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 table-fixed">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-56">Name / ID</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-20">Clicks</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-20">Signups</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-24">Purchased</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-28">Gross</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-32">Net (after TPT)</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-32">Net − Commission</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-32">Comm Paid</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-32">Comm Due</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-24">Created</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-60">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {affiliates.map(aff => {
                    const clicks = clickTotals[aff.id] ?? 0;
                    const sales = salesTotals[aff.id] || { gross: 0, netAfterTPT: 0, netMinusComm: 0, commissionPaid: 0, commissionDue: 0 };
                    const owners = Array.isArray(aff.owners) ? aff.owners : [];
                    return (
                      <React.Fragment key={aff.id}>
                        <tr>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleAffiliateExpand(aff)}
                                className="p-1 rounded hover:bg-slate-100 text-slate-600"
                                title={expandedAffiliates[aff.id] ? 'Collapse' : 'Expand'}
                              >
                                {expandedAffiliates[aff.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </button>
                              <div>
                                <div className="text-sm font-medium text-slate-900 truncate">{aff.name}</div>
                                <div className="text-xs text-slate-500 font-mono truncate">{aff.id}</div>
                                {owners.length > 0 && (
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {owners.slice(0, 2).map(o => (
                                      <span key={o.uid || o.email} className="px-2 py-0.5 text-[11px] rounded-full bg-slate-100 text-slate-600">
                                        {o.email || o.uid}
                                      </span>
                                    ))}
                                    {owners.length > 2 && (
                                      <span className="px-2 py-0.5 text-[11px] rounded-full bg-slate-100 text-slate-600">
                                        +{owners.length - 2} more
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center text-sm">{clicks}</td>
                          <td className="px-4 py-3 text-center text-sm">{signupCounts[aff.id] ?? 0}</td>
                          <td className="px-4 py-3 text-center text-sm">
                            <span className="inline-flex items-center gap-1">
                              <ShoppingCart className="w-4 h-4 text-green-600" />
                              <strong className="text-slate-900">{aff.totals?.purchases || 0}</strong>
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-sm">{money(sales.gross)}</td>
                          <td className="px-4 py-3 text-center text-sm">{money(sales.netAfterTPT)}</td>
                          <td className="px-4 py-3 text-center text-sm">{money(sales.netMinusComm)}</td>
                          <td className="px-4 py-3 text-center text-sm">{money(sales.commissionPaid)}</td>
                          <td className="px-4 py-3 text-center text-sm">{money(sales.commissionDue)}</td>
                          <td className="px-4 py-3 text-center text-sm">
                            {aff.createdAt?.toDate?.() ? aff.createdAt.toDate().toLocaleDateString() : '—'}
                          </td>
                          <td className="px-4 py-3 text-center text-sm">
                            <div className="flex items-center justify-center gap-3">
                              <button onClick={() => setLinkModalAffiliate(aff)} className="text-sky-600 hover:text-sky-900">Create Link</button>
                              <button onClick={() => handleViewLinks(aff)} className="text-slate-600 hover:text-slate-900">Links</button>
                              <button onClick={() => handleDownloadAffiliateCSV(aff)} className="text-slate-600 hover:text-slate-900 flex items-center gap-1">
                                <Download size={16} /> CSV
                              </button>
                            </div>
                          </td>
                        </tr>

                        {expandedAffiliates[aff.id] && (
                          <tr>
                            <td colSpan={11} className="bg-slate-50 px-6 py-4">
                              {/* Owners Management */}
                              <div className="mb-6 p-4 bg-white border rounded-lg">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                  <div>
                                    <h4 className="font-semibold text-slate-800">Owners</h4>
                                    <p className="text-xs text-slate-500">Owners can log in and view/manage this affiliate in the dashboard.</p>
                                  </div>
                                  <div className="flex gap-2">
                                    <input
                                      type="email"
                                      placeholder="owner@example.com"
                                      value={ownerEmailInputs[aff.id] || ''}
                                      onChange={(e) => handleOwnerInputChange(aff.id, e.target.value)}
                                      className="px-3 py-2 border rounded-md text-sm bg-white"
                                    />
                                    <button
                                      onClick={() => handleAddOwner(aff)}
                                      disabled={!!ownerBusy[aff.id]}
                                      className="px-3 py-2 text-sm rounded-md text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50"
                                    >
                                      {ownerBusy[aff.id] ? 'Adding…' : 'Add Owner'}
                                    </button>
                                  </div>
                                </div>

                                <div className="mt-3 flex flex-wrap gap-2">
                                  {(Array.isArray(aff.owners) ? aff.owners : []).map(o => (
                                    <span key={(o.uid || o.email) + '_chip'} className="inline-flex items-center gap-2 px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs">
                                      <span className="font-medium">{o.email || o.uid}</span>
                                      <button
                                        onClick={() => handleRemoveOwner(aff, o.email || '')}
                                        className="text-slate-500 hover:text-red-600"
                                        title="Remove owner"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </span>
                                  ))}
                                  {(Array.isArray(aff.owners) ? aff.owners : []).length === 0 && (
                                    <span className="text-xs text-slate-500">No owners yet.</span>
                                  )}
                                </div>
                                {!!ownerMsg[aff.id] && (
                                  <p className="mt-2 text-xs text-slate-500">{ownerMsg[aff.id]}</p>
                                )}
                              </div>

                              {/* Referred Users */}
                              {affReferredLoading[aff.id] ? (
                                <p className="text-sm text-slate-500">Loading referred users…</p>
                              ) : (affReferredUsers[aff.id]?.length ?? 0) === 0 ? (
                                <p className="text-sm text-slate-500">No users referred by this affiliate yet.</p>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-slate-200 table-fixed">
                                    <thead className="bg-white">
                                      <tr>
                                        <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-64">User Email</th>
                                        <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-28">Status</th>
                                        <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-40">Signed Up</th>
                                        <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-56">Purchase Confirmed (Approved At)</th>
                                        <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-32">Total Sale</th>
                                        <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-28">TPT 20%</th>
                                        <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-32">Net After TPT</th>
                                        <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-28">Rate</th>
                                        <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-32">Commission</th>
                                        <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-20">Paid</th>
                                        <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-64">UID</th>
                                        <th className="px-3 py-2 w-28"></th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-slate-200">
                                      {affReferredUsers[aff.id].map(u => {
                                        const row = getSaleRow(aff.id, u.id);
                                        const { tptFee, netAfterTPT, commissionDue } = computeValues(row.totalSale, row.commissionRate);
                                        return (
                                          <tr key={u.id}>
                                            <td className="px-3 py-2 text-sm text-slate-800 truncate" title={u.email}>{u.email}</td>
                                            <td className="px-3 py-2 text-sm text-center">
                                              {u.approvedByAdmin ? (
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Purchased</span>
                                              ) : (
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">Trial</span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-center">{dt(u.signedUpAt || u.createdAt)}</td>
                                            <td className="px-3 py-2 text-sm text-center">
                                              {u.approvedAt ? (
                                                <span title={dt(u.approvedAt)}>Purchase Confirmed<br /><span className="text-slate-500 text-xs">({dt(u.approvedAt)})</span></span>
                                              ) : (
                                                <button onClick={() => setManualApprovedAt(u.id)} className="text-xs text-sky-600 hover:text-sky-800 underline">
                                                  Set now
                                                </button>
                                              )}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-center">
                                              <input
                                                type="number"
                                                step="0.01"
                                                value={row.totalSale}
                                                onChange={(e) => setSaleRow(aff.id, u.id, { totalSale: e.target.value })}
                                                className="w-28 px-2 py-1 border rounded text-sm text-right"
                                                placeholder="0.00"
                                                title="Total sale"
                                              />
                                            </td>
                                            <td className="px-3 py-2 text-sm text-center">{money(isFinite(tptFee) ? tptFee : 0)}</td>
                                            <td className="px-3 py-2 text-sm text-center">{money(isFinite(netAfterTPT) ? netAfterTPT : 0)}</td>
                                            <td className="px-3 py-2 text-sm text-center">
                                              <input
                                                type="number"
                                                step="0.01"
                                                value={row.commissionRate}
                                                onChange={(e) => setSaleRow(aff.id, u.id, { commissionRate: e.target.value })}
                                                className="w-20 px-2 py-1 border rounded text-sm text-right"
                                                title="Commission rate (e.g., 0.20 = 20%)"
                                              />
                                            </td>
                                            <td className="px-3 py-2 text-sm text-center font-semibold">{money(isFinite(commissionDue) ? commissionDue : 0)}</td>
                                            <td className="px-3 py-2 text-sm text-center">
                                              <input
                                                type="checkbox"
                                                checked={!!row.paid}
                                                onChange={(e) => setSaleRow(aff.id, u.id, { paid: e.target.checked })}
                                              />
                                            </td>
                                            <td className="px-3 py-2 text-sm font-mono text-slate-500 truncate text-center" title={u.id}>{u.id}</td>
                                            <td className="px-3 py-2 text-center">
                                              <button
                                                onClick={() => saveSale(aff.id, u)}
                                                className="px-3 py-1 text-sm bg-sky-600 text-white rounded hover:bg-sky-700"
                                              >
                                                Save
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              {affiliates.length === 0 && (
                <p className="text-center py-8 text-slate-500">
                  {isFetchingAffiliates ? 'Loading affiliates...' : 'No affiliates created yet.'}
                </p>
              )}
            </div>
          </section>

          {/* Notifications */}
          <section className="mt-8 bg-white p-6 rounded-lg shadow-md border">
            <h2 className="text-xl font-semibold text-slate-800 flex items-center">
              <Bell className="w-6 h-6 mr-2 text-sky-600" />
              Global App Notifications
            </h2>

            <div className="mt-4 p-4 border rounded-lg bg-slate-50">
              <h3 className="font-semibold text-slate-700">Create New Notification</h3>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="newNotificationId" className="block text-sm font-medium text-slate-700">Unique Notification ID</label>
                  <input
                    id="newNotificationId"
                    value={newNotificationId}
                    onChange={(e) => setNewNotificationId(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm"
                    placeholder="e.g., new-feature-july-2025"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="newNotificationMessage" className="block text-sm font-medium text-slate-700">Message</label>
                  <div className="mt-1 bg-white">
                    <ReactQuill
                      theme="snow"
                      value={newNotificationMessage}
                      onChange={setNewNotificationMessage}
                      modules={{
                        toolbar: [
                          ['bold', 'italic', 'underline'],
                          [{ 'list': 'bullet' }, { 'list': 'ordered' }],
                          ['link']
                        ],
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-slate-700">Start Date (Optional)</label>
                  <input
                    type="date"
                    id="startDate"
                    value={newNotificationStartDate}
                    onChange={(e) => setNewNotificationStartDate(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm"
                  />
                </div>
                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-slate-700">End Date (Optional)</label>
                  <input
                    type="date"
                    id="endDate"
                    value={newNotificationEndDate}
                    onChange={(e) => setNewNotificationEndDate(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm"
                  />
                </div>
              </div>
              <div className="mt-3 flex justify-end items-center">
                <p className="text-sm text-slate-600 mr-4">{notificationStatus}</p>
                <button
                  onClick={handleCreateNotification}
                  className="px-4 py-2 text-sm rounded-md text-white bg-sky-600 hover:bg-sky-700 flex items-center gap-2"
                >
                  <PlusCircle size={16} /> Create & Activate
                </button>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {notifications.map(notif => (
                <div key={notif.id} className="bg-white border rounded-lg p-4 flex items-start justify-between">
                  <div>
                    <p className={`font-semibold ${notif.isActive ? 'text-slate-800' : 'text-slate-500'}`}>{notif.notificationId}</p>
                    <div
                      className={`prose prose-sm mt-1 ${notif.isActive ? 'text-slate-600' : 'text-slate-400'}`}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notif.message) }}
                    />
                    <div className="text-xs text-slate-400 mt-2 space-x-4">
                      <span>Created: {notif.createdAt?.toDate().toLocaleString()}</span>
                      {(notif.startDate || notif.endDate) && (
                        <span>
                          Scheduled: {notif.startDate ? notif.startDate.toDate().toLocaleDateString() : '...'} - {notif.endDate ? notif.endDate.toDate().toLocaleDateString() : '...'}
                        </span>
                      )}
                      <span>Views: {notif.viewCount || 0}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <div className="flex items-center">
                      <span className={`text-xs mr-2 ${notif.isActive ? 'text-green-600' : 'text-slate-500'}`}>{notif.isActive ? 'Active' : 'Inactive'}</span>
                      <button
                        onClick={() => handleToggleNotification(notif)}
                        className={`relative inline-flex h-6 w-11 rounded-full cursor-pointer transition-colors ${notif.isActive ? 'bg-green-500' : 'bg-slate-300'}`}
                      >
                        <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition ${notif.isActive ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                    </div>
                    <button onClick={() => handleDeleteNotification(notif.id)} title="Delete" className="p-2 text-slate-500 hover:text-red-600"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Update User Access */}
          <section className="mt-8 bg-white p-6 rounded-lg shadow-md border">
            <h2 className="text-xl font-semibold text-slate-800 flex items-center">
              <CalendarClock className="w-6 h-6 mr-2 text-sky-600" />
              Update User Access Expiration
            </h2>
            <p className="text-sm text-slate-500 mt-1">Grant or extend full access for a user until a specific date.</p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="updateUid" className="block text-sm font-medium text-slate-700">User ID (UID)</label>
                <input
                  id="updateUid"
                  value={updateUid}
                  onChange={(e) => setUpdateUid(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm"
                  placeholder="Paste User ID here"
                />
              </div>
              <div>
                <label htmlFor="newExpirationDate" className="block text-sm font-medium text-slate-700">New Expiration Date</label>
                <input
                  type="date"
                  id="newExpirationDate"
                  value={newExpirationDate}
                  onChange={(e) => setNewExpirationDate(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm"
                />
              </div>
              <div className="flex items-end">
                <button onClick={handleUpdateExpiration} className="w-full px-4 py-2 text-sm rounded-md text-white bg-sky-600 hover:bg-sky-700">
                  Update Expiration
                </button>
              </div>
            </div>
            {updateStatusMessage.text && (
              <p className={`mt-3 text-sm ${updateStatusMessage.isError ? 'text-red-600' : 'text-green-600'}`}>
                {updateStatusMessage.text}
              </p>
            )}
          </section>
        </main>
      </div>
    </>
  );
};

export default AdminPage;